import { tWithKeyAttr as t } from "../../i18nHelper";
import loginStyles from "./login.module.css";
import { KeyboardEvent, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../services/hooks";
import { errorUserSelector } from "../../services/selectors/auth.selectors";
import {
  authApi,
  useSendCodeWithoutLoginMutation,
} from "../../services/api/auth.api";
import { adminSelector } from "../../services/selectors/user.selectors";
import BackArrowButton from "../../components/backArrowButton/backArrowButton";
import Tooltip from "../../components/tooltip/tooltip";
import i18n from "../../i18n";
import {
  readIsFirst,
  resolvePostAuthPathAfter2FA,
} from "../../utils/postLoginRedirect";

const OTP_LENGTH = 6;
const INITIAL_STATE = ["", "", "", "", "", ""];

function LoginSmsCode() {
  const navigate = useNavigate();

  const admin = useAppSelector(adminSelector);
  const loginError = useAppSelector(errorUserSelector);

  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];

  const [nums, setNums] = useState(INITIAL_STATE);
  const [showSendCodeTooltip, setShowSendCodeTooltip] = useState(false);
  const [isSendDisabled, setIsSendDisabled] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);

  const [sendCode] = authApi.useAuthWithCodeMutation();
  const [authWithoutLogin] = authApi.useAuthWithoutLoginMutation();
  const [sendCodeWithoutLogin] = useSendCodeWithoutLoginMutation();

  const fillCode = useCallback((code: string, source: string) => {
    const digits = code.replace(/\D/g, "").slice(0, OTP_LENGTH).split("");
    if (digits.length === 0) return;
    const newNums = [...INITIAL_STATE];
    digits.forEach((d, i) => { newNums[i] = d; });
    setNums(newNums);
    inputRefs[Math.min(digits.length, OTP_LENGTH - 1)].current?.focus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const hasWebOTP = "OTPCredential" in window;
    if (!hasWebOTP) return;
    const ac = new AbortController();
    navigator.credentials
      .get({
        // @ts-expect-error Web OTP API is not in standard TypeScript lib yet
        otp: { transport: ["sms"] },
        signal: ac.signal,
      })
      .then((otp: unknown) => {
        const code = (otp as { code?: string })?.code;
        if (code) fillCode(code, "WebOTP");
      })
      .catch(() => {
      });
    return () => ac.abort();
  }, [fillCode]);

  useEffect(() => {
    if (!admin.id && !admin.phone) {
      navigate("/login");
    }
  }, [admin, navigate]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    const { key } = e;
    const currentInput = inputRefs[index].current;
    if (!currentInput) return;

    if (key >= "0" && key <= "9") {
      e.preventDefault();
      const newNums = [...nums];
      newNums[index] = key;
      setNums(newNums);

      if (index < inputRefs.length - 1) {
        inputRefs[index + 1].current?.focus();
      }
    }

    if (key === "Backspace") {
      e.preventDefault();
      const newNums = [...nums];

      if (newNums[index]) {
        newNums[index] = "";
        setNums(newNums);
      } else if (index > 0) {
        newNums[index - 1] = "";
        setNums(newNums);
        inputRefs[index - 1].current?.focus();
      }
    }

    if (key === "ArrowRight") {
      if (index < inputRefs.length - 1) {
        inputRefs[index + 1].current?.focus();
      }
    }

    if (key === "ArrowLeft") {
      if (index > 0) {
        inputRefs[index - 1].current?.focus();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").replace(/\D/g, "");
    if (pasteData.length > 0) {
      fillCode(pasteData, "paste");
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length > 1) {
      fillCode(value, "autofill");
    } else if (value.length === 1) {
      const newNums = [...nums];
      newNums[index] = value;
      setNums(newNums);
      if (index < inputRefs.length - 1) {
        inputRefs[index + 1].current?.focus();
      }
    }
  };

  const handleSubmit = () => {
    const code = nums.join("");
    const isEmailLoginFlow = Boolean(admin.email && admin.password);
    const payload = isEmailLoginFlow
      ? {
        code,
        usersId: admin.id,
        roleId: "0",
        email: admin.email,
        password: admin.password || "",
      }
      : { code, roleId: "0", phone: admin.phone || "" };
    const submitFn = isEmailLoginFlow ? sendCode : authWithoutLogin;
    // @ts-ignore
    submitFn(payload).then((response) => {
      if ("data" in response && response.data) {
        const accessToken =
          response.data.accessToken ?? response.data.AccessToken;
        if (!accessToken && !response.data.success) {
          setNums([...INITIAL_STATE]);
          if (inputRefs[0].current) inputRefs[0].current.focus();
          return;
        }
        setNums([...INITIAL_STATE]);
        const roleId = response.data?.user?.RoleId ?? response.data?.user?.roleId;
        navigate(resolvePostAuthPathAfter2FA(readIsFirst(response.data), roleId), {
          replace: true,
        });
      } else {
        setNums([...INITIAL_STATE]);
        if (inputRefs[0].current) inputRefs[0].current.focus();
      }
    });
  };

  useEffect(() => {
    if (countdown === null) return;

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev && prev > 1) return prev - 1;
        clearInterval(interval);
        setIsSendDisabled(false);
        return null;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [countdown]);

  useEffect(() => {
    if (nums.every((num) => num !== "")) {
      setTimeout(() => {
        handleSubmit();
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nums]);

  const sendCodeAgain = () => {
    sendCodeWithoutLogin({ phone: admin.phone, roleId: "0", code: "0" }).then(
      (res) => {
        if ("data" in res) {
          setShowSendCodeTooltip(true);
          setIsSendDisabled(true);
          setCountdown(30);
        }
      }
    );
    setTimeout(() => {
      setShowSendCodeTooltip(false);
    }, 1500);
    setTimeout(() => setIsSendDisabled(false), 30000);
  };

  return (
    <article className={loginStyles.login} dir={i18n.dir()} data-testid="enter-verification-code-page">
      <BackArrowButton text={t('PREVIOUS_SCREEN')} onClick={() => navigate("/login")} />
      <h1
        className={`${loginStyles.login__title} ${loginStyles.login__title_sms}`}
      >
        {t('WE_MISSED_YOU')}</h1>
      <p
        className={`${loginStyles.login__text} ${loginStyles.login__text_small}`}
      >
        {t('SENT_VERIFICATION_CODE_SMS')}{admin.phone?.slice(-3)}
      </p>
      <label htmlFor="num1" className={loginStyles.login__enterCodeLabel}>
        {t('ENTER_VERIFICATION_CODE')}</label>
      <ul className={loginStyles.login__enterCode}>
        {nums.map((num, index) => (
          <input
            key={index}
            ref={inputRefs[index]}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
            onChange={(e) => handleChange(e, index)}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete={index === 0 ? "one-time-code" : "off"}
            name={`otp-${index}`}
            id={`otp-${index}`}
            className={loginStyles.login__number}
            placeholder="__"
            value={nums[index]}
            autoFocus={index === 0}
          />
        ))}
      </ul>
      {(loginError) && (
        <p className={`${loginStyles.login__invalid_message}`}>{loginError}</p>
      )}
      <p className={loginStyles.login__sendAgain}>
        {t('DID_NOT_RECEIVE_CODE')}<span
          className={`${loginStyles.login__sendAgain_underlined} hover`}
          onClick={sendCodeAgain}
          style={{
            pointerEvents: isSendDisabled ? "none" : "auto",
            opacity: isSendDisabled ? 0.6 : 1,
          }}
        >
          {t('RESEND')}<Tooltip isShown={showSendCodeTooltip}>{t('SHALCHNU_SHUV')}</Tooltip>
        </span>
        {isSendDisabled && countdown !== null && (
          <span className={loginStyles.login__countdown}>
            {t('ANOTHER_SEND_WILL_BE_ALLOWED')}{countdown} {t('SECONDS')}</span>
        )}
      </p>
    </article>
  );
}

export default LoginSmsCode;
